import { useEffect, useRef } from 'react';
import { getAudioAnalyser } from '../store/radioStore';

interface AudioVisualizerProps {
  isPlaying: boolean;
}

export default function AudioVisualizer({ isPlaying }: AudioVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);

  // Animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);

      // Get canvas dimensions
      const width = canvas.width;
      const height = canvas.height;

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Get analyser from radio store
      const analyser = getAudioAnalyser();

      if (!isPlaying || !analyser) {
        // Draw idle state - subtle pulsing circles
        const time = Date.now() / 1000;
        const centerX = width / 2;
        const centerY = height / 2;

        for (let i = 0; i < 3; i++) {
          const radius = 100 + i * 80 + Math.sin(time + i) * 20;
          const alpha = 0.1 - i * 0.03;
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(139, 92, 246, ${alpha})`;
          ctx.lineWidth = 2;
          ctx.stroke();
        }
        return;
      }

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      // Get frequency data
      analyser.getByteFrequencyData(dataArray);

      // Calculate average for background pulse
      const average = dataArray.reduce((a, b) => a + b, 0) / bufferLength;
      const normalizedAvg = average / 255;

      // Draw background pulse
      const gradient = ctx.createRadialGradient(
        width / 2, height / 2, 0,
        width / 2, height / 2, height * 0.6
      );
      gradient.addColorStop(0, `rgba(139, 92, 246, ${normalizedAvg * 0.3})`);
      gradient.addColorStop(0.5, `rgba(236, 72, 153, ${normalizedAvg * 0.15})`);
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      // Draw circular visualization
      const centerX = width / 2;
      const centerY = height / 2;
      const baseRadius = Math.min(width, height) * 0.25;

      // Draw frequency bars in a circle
      const bars = 64;
      const step = Math.floor(bufferLength / bars);

      for (let i = 0; i < bars; i++) {
        const value = dataArray[i * step];
        const normalizedValue = value / 255;
        const barHeight = normalizedValue * baseRadius * 0.8;

        const angle = (i / bars) * Math.PI * 2 - Math.PI / 2;

        const x1 = centerX + Math.cos(angle) * baseRadius;
        const y1 = centerY + Math.sin(angle) * baseRadius;
        const x2 = centerX + Math.cos(angle) * (baseRadius + barHeight);
        const y2 = centerY + Math.sin(angle) * (baseRadius + barHeight);

        // Gradient color based on frequency
        const hue = 270 + (i / bars) * 60; // purple to pink
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.strokeStyle = `hsla(${hue}, 80%, 60%, ${0.5 + normalizedValue * 0.5})`;
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.stroke();
      }

      // Inner circle glow
      const innerGradient = ctx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, baseRadius
      );
      innerGradient.addColorStop(0, `rgba(139, 92, 246, ${0.2 + normalizedAvg * 0.3})`);
      innerGradient.addColorStop(0.7, `rgba(236, 72, 153, ${0.1 + normalizedAvg * 0.2})`);
      innerGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = innerGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, baseRadius, 0, Math.PI * 2);
      ctx.fill();

      // Outer ring
      ctx.beginPath();
      ctx.arc(centerX, centerY, baseRadius, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(139, 92, 246, ${0.3 + normalizedAvg * 0.4})`;
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw waveform ring
      const waveRadius = baseRadius * 1.5;
      ctx.beginPath();
      for (let i = 0; i <= bars; i++) {
        const value = dataArray[(i % bars) * step];
        const normalizedValue = value / 255;
        const wobble = normalizedValue * 30;

        const angle = (i / bars) * Math.PI * 2 - Math.PI / 2;
        const x = centerX + Math.cos(angle) * (waveRadius + wobble);
        const y = centerY + Math.sin(angle) * (waveRadius + wobble);

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.closePath();
      ctx.strokeStyle = `rgba(236, 72, 153, ${0.2 + normalizedAvg * 0.3})`;
      ctx.lineWidth = 1;
      ctx.stroke();

      // Floating particles based on bass
      const bass = dataArray.slice(0, 4).reduce((a, b) => a + b, 0) / 4 / 255;
      const particleCount = Math.floor(bass * 20);
      const time = Date.now() / 1000;

      for (let i = 0; i < particleCount; i++) {
        const particleAngle = (time * 0.5 + i * 0.5) % (Math.PI * 2);
        const particleRadius = baseRadius * 1.8 + Math.sin(time * 2 + i) * 50;
        const px = centerX + Math.cos(particleAngle) * particleRadius;
        const py = centerY + Math.sin(particleAngle) * particleRadius;
        const size = 2 + bass * 4;

        ctx.beginPath();
        ctx.arc(px, py, size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(139, 92, 246, ${0.3 + bass * 0.5})`;
        ctx.fill();
      }
    };

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying]);

  // Handle canvas resize
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full pointer-events-none z-0"
    />
  );
}
